// frontend-react/src/pages/AdReviewPage.tsx
import React, { useState, useCallback } from 'react';
import { Layout, Card, Button, Upload, Space, Progress, Spin } from 'antd';
import {
  InboxOutlined,
  HistoryOutlined,
  DownloadOutlined,
  SaveOutlined,
  ExperimentOutlined,
  PictureOutlined,
} from '@ant-design/icons';

import PageLayout from '../components/PageLayout';
import SystemStatus from '../components/SystemStatus';
import DisclaimerBanner from '../components/DisclaimerBanner';
import PDFViewer from '../components/PDFViewer';
import ReviewResults from '../components/ReviewResults';
import ReviewHistoryWithShare from '../components/ReviewHistoryWithShare';

import RuleUploadModal from '../components/RuleManagement/RuleUploadModal';
import RuleSelector from '../components/RuleManagement/RuleSelector';
import DepartmentAssignModal from '../components/RuleManagement/DepartmentAssignModal';
import RulePreviewModal from '../components/RuleManagement/RulePreviewModal';
import ValidationModal from '../components/ValidationModal';
import BatchValidationModal from '../components/BatchValidationModal';
import ImageDescriptionModal from '../components/ImageDescriptionModal';

import { useAdReviewController } from './adReview/useAdReviewController';
import { useFeatureFlags } from '../hooks/useFeatureFlags';

const { Content } = Layout;
const { Dragger } = Upload;

// ⭐ 保留原邏輯：計算可用高度，減去 header + 副標題 + 操作按鈕區 + padding
const REVIEW_PANEL_HEIGHT = 'max(700px, calc(100vh - 280px))';

const AdReviewPage: React.FC = () => {
  const c = useAdReviewController();
  const {
    riskLevelEnabled,
    validationMode,
    canToggleValidationMode,
    canEditReviewCards,
    imageDescriptionEnabled,
  } = useFeatureFlags();
  const [batchValidationVisible, setBatchValidationVisible] = useState(false);
  const [imageDescModalVisible, setImageDescModalVisible] = useState(false);
  const [pdfScale, setPdfScale] = useState(1.0);
  const handlePdfScaleChange = useCallback((s: number) => setPdfScale(s), []);

  // 根據 PDF 縮放比例動態計算左右欄寬度比
  const pdfColumnFr = Math.max(1, Math.round(pdfScale * 4)); // 0.5→2, 0.75→3, 1.0→4
  const reviewColumnFr = 4; // 固定基準
  const gridColumns =
    c.pdfData || c.isLoadingHistoryFile || c.isReviewing
      ? `${pdfColumnFr}fr ${reviewColumnFr}fr`
      : '1fr';

  // 兩側面板已出現（含歷史載入中、審閱中、有結果、有 PDF）→ 隱藏上方上傳區域
  const showingResultPanel = !!(
    c.pdfData ||
    c.isLoadingHistoryFile ||
    c.reviewResults.length > 0 ||
    c.isReviewing
  );

  return (
    <PageLayout currentPage='/ad-review'>
      {/* 副標題區域 */}
      <div
        style={{
          padding: '16px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div>
          <h1 style={{ margin: 0, fontSize: '24px', color: 'var(--color-text)' }}>全文審閱系統</h1>
          <p style={{ margin: '4px 0 0 0', color: '#6b7280' }}>
            ① 選擇規則集 → ② 上傳待審文件（新聞稿、廣宣）→ ③ 點擊「開始審閱」
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <SystemStatus />
        </div>
      </div>

      <Content
        style={{
          padding: '24px',
          maxWidth: '1400px',
          margin: '0 auto',
          width: '100%',
          background: 'var(--color-bg)',
        }}
      >
        <Space direction='vertical' style={{ width: '100%' }} size='large'>
          {/* 規則選擇區域：僅在兩側面板未出現時顯示完整版 */}
          {!showingResultPanel && (
            <RuleSelector
              ruleSets={c.ruleSets}
              manageRuleSets={c.manageRuleSets}
              selectedRuleSetId={c.ruleSetId}
              onRuleSetChange={c.setRuleSetId}
              selectedValue={c.selectRuleValue}
              onValueChange={c.handleRuleSelectChange}
              onUploadClick={c.openUploadRuleModal}
              onShareClick={c.openShareModal}
              onPreviewClick={c.openPreviewModal}
              onRefresh={c.loadRuleSets}
              isLoading={c.isLoadingRules}
              canUploadRules={c.canUploadRules}
              canShareRules={c.canShareRules}
              isAdmin={c.isAdmin}
              isSuperAdmin={c.isSuperAdmin}
              userId={c.user?.id}
              showAiOption={c.canUseAiRules}
              canUseValidationMode={canToggleValidationMode}
              validationMode={validationMode}
              onValidationModeToggle={c.handleValidationModeToggle}
              userPreferredRuleSetId={c.user?.preferred_rule_set_id ?? null}
              onSetUserPreferred={c.handleSetUserPreferred}
            />
          )}

          {/* Upload Section（兩側面板出現後隱藏） */}
          {!showingResultPanel && (
            <>
              <Dragger
                name='file'
                multiple={false}
                accept='.pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                beforeUpload={c.handleReviewFileUpload}
                showUploadList={false}
              >
                <p className='ant-upload-drag-icon'>
                  <InboxOutlined style={{ fontSize: '52px', color: 'var(--color-warning)' }} />
                </p>
                <p className='ant-upload-text'>
上傳待審閱文件
                </p>
                <p className='ant-upload-hint'>支援 Word（.docx）與 PDF 檔案，檔案上限為 10 MB</p>
              </Dragger>

              <div style={{ textAlign: 'center' }}>
                <Space size='middle' wrap style={{ justifyContent: 'center' }}>
                  {imageDescriptionEnabled && (
                    <Button
                      icon={<PictureOutlined />}
                      onClick={() => setImageDescModalVisible(true)}
                      size='large'
                      type='primary'
                      style={{
                        fontSize: '16px',
                        height: '48px',
                        paddingLeft: '20px',
                        paddingRight: '20px',
                      }}
                    >
                      圖片轉文字
                    </Button>
                  )}
                  <Button
                    icon={<HistoryOutlined />}
                    onClick={c.showHistory}
                    size='large'
                    type='primary'
                    style={{
                      fontSize: '16px',
                      height: '48px',
                      paddingLeft: '20px',
                      paddingRight: '20px',
                    }}
                  >
                    歷史記錄
                  </Button>
                  {validationMode && (
                    <Button
                      icon={<ExperimentOutlined />}
                      onClick={() => setBatchValidationVisible(true)}
                      size='large'
                      style={{
                        fontSize: '16px',
                        height: '48px',
                        paddingLeft: '20px',
                        paddingRight: '20px',
                        background: '#4a5e3a',
                        borderColor: '#4a5e3a',
                        color: '#fff',
                      }}
                    >
                      批次驗測
                    </Button>
                  )}
                </Space>
              </div>
            </>
          )}

          {/* 操作按鈕區域（有上傳檔案 / 有 PDF / 正在審閱中） */}
          {(c.uploadedFile || c.pdfData || c.isReviewing) && (
            <Card>
              {/* 左上角：規則選擇（compact 模式，選項定義在 RuleSelector） */}
              <div style={{ marginBottom: '12px' }}>
                <RuleSelector
                  compact
                  ruleSets={c.ruleSets}
                  selectedRuleSetId={c.ruleSetId}
                  onRuleSetChange={c.setRuleSetId}
                  selectedValue={c.selectRuleValue}
                  onValueChange={c.handleRuleSelectChange}
                  onUploadClick={c.openUploadRuleModal}
                  onShareClick={c.openShareModal}
                  onPreviewClick={c.openPreviewModal}
                  onRefresh={c.loadRuleSets}
                  isLoading={c.isLoadingRules}
                  showAiOption={c.canUseAiRules}
                  userPreferredRuleSetId={c.user?.preferred_rule_set_id ?? null}
                  onSetUserPreferred={c.handleSetUserPreferred}
                  historyRuleSetName={c.historyRuleSetName}
                />
              </div>

              <div style={{ textAlign: 'center' }}>
                <Space wrap>
                  <Button
                    type='primary'
                    size='large'
                    onClick={c.handleReview}
                    loading={c.isReviewing}
                    disabled={c.isReviewing}
                    style={{ height: '40px' }}
                  >
                    {c.isReviewing ? '分析中...' : '開始審閱'}
                  </Button>

                  <Button
                    icon={<HistoryOutlined />}
                    size='large'
                    onClick={c.showHistory}
                    style={{ height: '40px' }}
                  >
                    歷史記錄
                  </Button>

                  <Button
                    icon={<SaveOutlined />}
                    size='large'
                    onClick={c.handleSaveReview}
                    disabled={c.activeResults.length === 0}
                    type='default'
                    style={{ height: '40px', borderColor: '#52c41a', color: '#52c41a' }}
                    title='保存審查記錄到資料庫'
                  >
                    保存記錄
                  </Button>

                  <Button
                    icon={<DownloadOutlined />}
                    size='large'
                    onClick={c.handleDownloadWordReport}
                    disabled={c.activeResults.length === 0}
                    style={{ height: '40px' }}
                    title='下載附評論的 Word 報告'
                  >
                    Word 報告
                  </Button>

                  {validationMode &&
                    (() => {
                      const isDisabled = c.activeResults.length === 0 || c.isReviewing;
                      return (
                        <Button
                          size='large'
                          onClick={c.handleValidationReport}
                          disabled={isDisabled}
                          title='計算審閱準確率與誤報率'
                          style={{
                            height: '40px',
                            ...(isDisabled
                              ? {}
                              : {
                                  background: '#6B7B5A',
                                  borderColor: '#6B7B5A',
                                  color: '#fff',
                                }),
                          }}
                        >
                          自動驗測
                        </Button>
                      );
                    })()}

                  <Button type='text' onClick={c.resetUpload} title='重新上傳文件'>
                    🔄 重新上傳
                  </Button>
                </Space>

                {(c.uploadedFile || c.isReviewing) && (
                  <div style={{ marginTop: '12px' }}>
                    <span style={{ color: 'var(--color-success)', fontSize: '14px' }}>
                      ✓ 已上傳：{c.uploadedFile?.name ?? '（恢復中...）'}
                    </span>
                  </div>
                )}

                {/* 進度條顯示 */}
                {c.isReviewing && (
                  <div
                    style={{
                      marginTop: '20px',
                      padding: '16px',
                      background: '#f5f5f5',
                      borderRadius: '8px',
                    }}
                  >
                    <div
                      style={{
                        marginBottom: '8px',
                        color: '#1E88A8',
                        fontSize: '14px',
                        fontWeight: 500,
                      }}
                    >
                      {c.reviewStage}
                    </div>
                    <Progress
                      percent={c.reviewProgress}
                      strokeColor={{ '0%': '#108ee9', '100%': '#87d068' }}
                      status={c.reviewProgress === 100 ? 'success' : 'active'}
                      size='default'
                    />
                    {c.connectionWarning && (
                      <div
                        style={{
                          marginTop: '6px',
                          color: '#faad14',
                          fontSize: '12px',
                          textAlign: 'center',
                        }}
                      >
                        ⚠️ 網路連線不穩定，系統仍在嘗試重新連線...
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Main Content（有 PDF / 有結果 / 載入歷史 / 正在審閱中都顯示） */}
          {(c.pdfData || c.isLoadingHistoryFile || c.reviewResults.length > 0 || c.isReviewing) && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: gridColumns,
                gap: '24px',
                minHeight: '600px',
              }}
            >
              {/* PDF Viewer */}
              {c.pdfData ? (
                <Card
                  title={
                    <span>
                      <span style={{ marginRight: '8px' }}>📋</span>
                      原始文件內容
                    </span>
                  }
                  styles={{
                    body: {
                      height: REVIEW_PANEL_HEIGHT,
                      padding: 0,
                      display: 'flex',
                      flexDirection: 'column',
                    },
                  }}
                >
                  <PDFViewer
                    pdfData={c.pdfData}
                    reviewResults={c.activeResults}
                    onScaleChange={handlePdfScaleChange}
                  />
                </Card>
              ) : c.isLoadingHistoryFile || c.isReviewing ? (
                <Card
                  title={
                    <span>
                      <span style={{ marginRight: '8px' }}>📋</span>
                      原始文件內容
                    </span>
                  }
                  styles={{
                    body: {
                      height: REVIEW_PANEL_HEIGHT,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    },
                  }}
                >
                  <Spin size='large' tip={c.isReviewing ? 'AI 審閱中...' : '載入文件中...'} />
                </Card>
              ) : null}

              {/* Review Results */}
              <Card
                title={
                  <span>
                    <span style={{ marginRight: '8px' }}>⚖️</span>
                    AI 審閱建議
                  </span>
                }
                style={{
                  gridColumn:
                    c.pdfData || c.isLoadingHistoryFile || c.isReviewing ? 'auto' : '1 / -1',
                }}
                styles={{
                  body: {
                    height: REVIEW_PANEL_HEIGHT,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                  },
                }}
              >
                {!c.pdfData && !c.isLoadingHistoryFile && c.reviewResults.length > 0 && (
                  <div
                    style={{
                      padding: '12px 16px',
                      background: '#fff7e6',
                      border: '1px solid #ffd591',
                      borderRadius: '4px',
                      marginBottom: '16px',
                      color: '#ad6800',
                      flexShrink: 0,
                    }}
                  >
                    <strong>📝 提示：</strong>
                    此記錄沒有原始檔案預覽。如需查看原始文件，請重新上傳檔案。
                  </div>
                )}

                {c.reviewResults.length > 0 && (
                  <div style={{ flexShrink: 0 }}>
                    <DisclaimerBanner />
                  </div>
                )}

                <div
                  style={{
                    flex: 1,
                    minHeight: 0,
                    marginTop: c.reviewResults.length > 0 ? 4 : 0,
                    overflow: 'auto',
                  }}
                >
                  <ReviewResults
                    results={c.activeResults}
                    isLoading={c.isReviewing}
                    onDeleteCard={canEditReviewCards ? c.handleDeleteCard : undefined}
                    onEditCard={canEditReviewCards ? c.handleEditCard : undefined}
                    onRestoreCard={canEditReviewCards ? c.handleRestoreCard : undefined}
                    editedItemIds={canEditReviewCards ? new Set(c.editedItems.keys()) : undefined}
                    fileName={c.uploadedFile?.name}
                    showRiskLevel={riskLevelEnabled}
                    ruleOptions={canEditReviewCards ? c.ruleOptions : undefined}
                  />
                </div>
              </Card>
            </div>
          )}
        </Space>
      </Content>

      {/* 歷史 */}
      <ReviewHistoryWithShare
        visible={c.historyVisible}
        onClose={() => c.setHistoryVisible(false)}
        onLoadHistory={c.loadHistoryDetail}
      />

      {/* 規則上傳 */}
      <RuleUploadModal
        visible={c.uploadRuleModalVisible}
        onClose={c.closeUploadRuleModal}
        onSuccess={c.loadRuleSets}
        isAdmin={c.isAdmin}
        showAiMode={c.canUploadAiRuleset}
      />

      {/* 部門指派 */}
      <DepartmentAssignModal
        visible={c.shareModalVisible}
        ruleSet={c.selectedRuleForShare}
        onClose={c.closeShareModal}
      />

      {/* 規則預覽 */}
      <RulePreviewModal
        visible={c.previewModalVisible}
        ruleSet={c.selectedRuleForPreview}
        onClose={c.closePreviewModal}
        onRuleSetNameChange={c.handleRuleSetNameChange}
      />

      {/* 自動驗測 */}
      <ValidationModal
        visible={c.validationModalVisible}
        activeResults={c.activeResults}
        onClose={c.closeValidationModal}
        fileName={c.uploadedFile?.name}
      />

      {/* 批次驗測 */}
      <BatchValidationModal
        visible={batchValidationVisible}
        onClose={() => setBatchValidationVisible(false)}
        userPreferredRuleSetId={c.user?.preferred_rule_set_id ?? null}
      />

      {/* 圖片說明 */}
      <ImageDescriptionModal
        visible={imageDescModalVisible}
        onClose={() => setImageDescModalVisible(false)}
      />
    </PageLayout>
  );
};

export default AdReviewPage;
